import React, { useState, useEffect, useMemo } from 'react';

const SPREADSHEET_STYLES = `
  .ss-table { border-collapse: collapse; font-size: 13px; font-family: Arial, Helvetica, sans-serif; min-width: 100%; }
  .ss-table td, .ss-table th { border: 1px solid #d0d7de; padding: 4px 8px; white-space: nowrap; }
  .ss-table tr:first-child td { background: #c6efce; font-weight: 600; color: #145a32; }
  .ss-table tr:nth-child(even) td { background: #f4faf4; }
  .ss-table tr:hover td { background: #e8f5e9; }
`;

const SpreadsheetViewer = ({ artifactUrl, filename }) => {
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWorkbook(null);
    setActiveSheet(0);

    fetch(artifactUrl)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(ab => {
        if (cancelled) return;
        const XLSX = require('xlsx');
        const wb = XLSX.read(ab, { type: 'array', cellDates: true, dense: false });
        setWorkbook(wb);
        setLoading(false);
      })
      .catch(err => {
        if (!cancelled) { setError(err.message); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [artifactUrl]);

  const sheetHtml = useMemo(() => {
    if (!workbook) return '';
    const XLSX = require('xlsx');
    const sheetName = workbook.SheetNames[activeSheet];
    if (!sheetName) return '';
    const sheet = workbook.Sheets[sheetName];
    // sheet_to_html throws on sheets with no !ref (completely empty sheets)
    if (!sheet || !sheet['!ref']) {
      return '<table class="ss-table"><tr><td style="color:#999;font-style:italic;padding:16px;">This sheet is empty.</td></tr></table>';
    }
    try {
      return XLSX.utils.sheet_to_html(sheet, { id: null, editable: false })
        .replace('<table', '<table class="ss-table"');
    } catch (e) {
      console.error('sheet_to_html error:', e.message);
      return '<table class="ss-table"><tr><td style="color:#999;font-style:italic;padding:16px;">Could not render this sheet.</td></tr></table>';
    }
  }, [workbook, activeSheet]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>
        Loading spreadsheet…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
        <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>Could not display spreadsheet inline.</p>
        <a href={artifactUrl} download={filename} style={{ color: '#86efac', fontSize: 15 }}>
          ⬇ Download {filename}
        </a>
      </div>
    );
  }

  const sheets = workbook?.SheetNames || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <style>{SPREADSHEET_STYLES}</style>

      {/* Sheet tabs (only shown when multiple sheets) */}
      {sheets.length > 1 && (
        <div style={{ display: 'flex', background: '#155a35', padding: '6px 8px 0', gap: 4, flexShrink: 0, overflowX: 'auto' }}>
          {sheets.map((name, i) => (
            <button
              key={name}
              onClick={() => setActiveSheet(i)}
              style={{
                background: i === activeSheet ? 'white' : 'rgba(255,255,255,0.15)',
                color: i === activeSheet ? '#155a35' : 'white',
                border: 'none',
                padding: '5px 14px',
                borderRadius: '4px 4px 0 0',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: i === activeSheet ? '600' : '400',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* Scrollable cell grid */}
      <div
        style={{ flex: 1, overflow: 'auto', background: 'white', padding: '0' }}
        dangerouslySetInnerHTML={{ __html: sheetHtml }}
      />

      {/* Footer download bar */}
      <div style={{
        background: '#155a35',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: 12,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
          {sheets.length > 0 ? `${sheets.length} sheet${sheets.length !== 1 ? 's' : ''}` : ''}
        </span>
        <a
          href={artifactUrl}
          download={filename}
          style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: '600',
            background: 'rgba(255,255,255,0.15)',
            padding: '4px 14px',
            borderRadius: 4,
          }}
        >
          ⬇ Download
        </a>
      </div>
    </div>
  );
};

export default SpreadsheetViewer;
